'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { Users, Award, BookOpen, Shield, Handshake } from 'lucide-react';

const trustPillars = [
  {
    icon: Users,
    title: 'Industry Advisory Council',
    desc: 'Senior real estate professionals guiding curriculum design and ensuring programme relevance across the African property sector.',
    href: '/advisory-council',
    linkLabel: 'Meet the Council',
  },
  {
    icon: BookOpen,
    title: 'Corporate Training',
    desc: 'Bespoke training solutions for estate agencies, property developers, financial institutions, and public sector organisations.',
    href: '/corporate-training',
    linkLabel: 'Explore Services',
  },
  {
    icon: Award,
    title: 'Professional Certifications',
    desc: 'Structured programmes designed to build verifiable professional credentials recognised across the South African property industry.',
    href: '/course-marketplace',
    linkLabel: 'View Programmes',
  },
  {
    icon: Shield,
    title: 'Accreditation Journey',
    desc: 'REUSA is actively pursuing formal NQF alignment, PPRA recognition, and SAQA registration. Transparency is central to our governance.',
    href: '/#accreditation',
    linkLabel: 'Accreditation Status',
  },
  {
    icon: Handshake,
    title: 'Industry Partnerships',
    desc: 'Building relationships with leading property organisations, professional bodies, and industry associations across the continent.',
    href: '/advisory-council',
    linkLabel: 'Our Partners',
  },
];

export default function TrustSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const els = ref?.current?.querySelectorAll('.section-reveal, .stagger-child');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-20 bg-primary relative overflow-hidden" ref={ref} id="trust">
      {/* Gold top line */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, #c9a84c 30%, #e8c96a 50%, #c9a84c 70%, transparent 100%)' }} />
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        {/* Header */}
        <div className="text-center mb-14 section-reveal">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-accent/60" />
            <span className="font-sans text-[0.65rem] font-semibold tracking-widest uppercase text-accent/80">Institutional Foundation</span>
            <div className="h-px w-12 bg-accent/60" />
          </div>
          <h2 className="font-serif text-white mb-4" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', lineHeight: 1.15, letterSpacing: '-0.015em' }}>
            Built With Industry.<br />
            <span className="italic text-accent">Focused On Impact.</span>
          </h2>
          <p className="font-sans text-white/55 max-w-xl mx-auto leading-relaxed text-sm">
            REUSA is built on a foundation of industry governance, professional rigour, and a long-term
            commitment to the development of African real estate professionals.
          </p>
        </div>

        {/* Pillars — asymmetric bento layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {trustPillars?.slice(0, 3)?.map((pillar, i) => (
            <div
              key={pillar?.title}
              className="stagger-child bg-white/5 border border-white/10 rounded-2xl p-7 group hover:bg-white/8 hover:border-accent/30 transition-all duration-300"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-11 h-11 rounded-xl bg-white/8 border border-white/10 flex items-center justify-center mb-5 group-hover:bg-accent/20 group-hover:border-accent/30 transition-all duration-300">
                <pillar.icon size={20} className="text-accent" />
              </div>
              <h3 className="font-serif font-semibold text-white text-lg mb-3">{pillar?.title}</h3>
              <p className="font-sans text-sm text-white/55 leading-relaxed mb-5">{pillar?.desc}</p>
              <Link
                href={pillar?.href}
                className="font-sans text-xs font-semibold tracking-widest uppercase text-accent/70 hover:text-accent transition-colors flex items-center gap-2"
                style={{ letterSpacing: '0.1em' }}
              >
                {pillar?.linkLabel}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          ))}
          {/* Bottom two — wider */}
          {trustPillars?.slice(3)?.map((pillar, i) => (
            <div
              key={pillar?.title}
              className="stagger-child bg-white/5 border border-white/10 rounded-2xl p-7 group hover:bg-white/8 hover:border-accent/30 transition-all duration-300"
              style={{ transitionDelay: `${(i + 3) * 80}ms` }}
            >
              <div className="w-11 h-11 rounded-xl bg-white/8 border border-white/10 flex items-center justify-center mb-5 group-hover:bg-accent/20 group-hover:border-accent/30 transition-all duration-300">
                <pillar.icon size={20} className="text-accent" />
              </div>
              <h3 className="font-serif font-semibold text-white text-lg mb-3">{pillar?.title}</h3>
              <p className="font-sans text-sm text-white/55 leading-relaxed mb-5">{pillar?.desc}</p>
              <Link
                href={pillar?.href}
                className="font-sans text-xs font-semibold tracking-widest uppercase text-accent/70 hover:text-accent transition-colors flex items-center gap-2"
                style={{ letterSpacing: '0.1em' }}
              >
                {pillar?.linkLabel}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-10 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6 section-reveal">
          <p className="font-sans text-sm text-white/45 max-w-lg leading-relaxed">
            REUSA is an independent professional and executive education provider. We are committed to
            transparency in our accreditation journey and governance.
          </p>
          <div className="flex gap-4 flex-shrink-0">
            <Link href="/founders-letter" className="font-sans text-xs font-semibold tracking-widest uppercase text-white/50 hover:text-white transition-colors" style={{ letterSpacing: '0.1em' }}>
              Founder&apos;s Letter
            </Link>
            <span className="text-white/20">|</span>
            <Link href="/advisory-council" className="font-sans text-xs font-semibold tracking-widest uppercase text-white/50 hover:text-white transition-colors" style={{ letterSpacing: '0.1em' }}>
              Advisory Council
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
